/**
 * Boot configuration, read from the `<script>` tag that loaded this bundle.
 */
export interface WidgetConfig {
  projectKey: string;
  apiUrl: string;
}

/**
 * Locates the script element that loaded the widget.
 *
 * `document.currentScript` is correct during synchronous execution but is
 * `null` for `async`/`defer` scripts — which is exactly how the install
 * snippet loads it — so a `data-project` lookup is the fallback.
 */
function findOwnScript(): HTMLScriptElement | null {
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement && current.dataset.project) {
    return current;
  }

  return document.querySelector<HTMLScriptElement>('script[data-project]');
}

/**
 * Derives the API origin from the script's own `src`, so a customer only ever
 * configures one thing: the project key. An explicit `data-api` overrides it,
 * which is what the local demo page uses.
 */
function resolveApiUrl(script: HTMLScriptElement): string {
  const override = script.dataset.api;
  if (override) return override.replace(/\/$/, '');

  try {
    return new URL(script.src).origin;
  } catch {
    return window.location.origin;
  }
}

export function readConfig(): WidgetConfig | null {
  const script = findOwnScript();
  if (!script) return null;

  const projectKey = script.dataset.project;
  if (!projectKey) return null;

  return { projectKey, apiUrl: resolveApiUrl(script) };
}
